import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { CountryNameService, RegionNameService } from 'gain-lib/geography';
import { CountryRegionDataService } from 'gain-web/shared-modules/country-region-data/country-region-data.service';
import { ApiClient } from 'gain-web/shared-services/api-client.generated.service';
import { Observable, combineLatest, of } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  skip,
  startWith,
  takeUntil,
} from 'rxjs/operators';

export interface RegionOptionGroup {
  countryCode: string;
  countryName: string;
  regions: ApiClient.RegionDto[];
}

@Injectable({ providedIn: 'root' })
export class LocationFormHelper {
  constructor(
    private _countryRegionDataService: CountryRegionDataService,
    private _countryName: CountryNameService,
    private _regionName: RegionNameService,
  ) {}

  // excludeGeneric: opt-in per field. Removes generic/placeholder countries (e.g. XX)
  // from the options without changing the shared country list (US 7568852).
  public getCountryOptions(
    control: FormControl<any>,
    opts: { excludeGeneric?: boolean } = {},
  ) {
    return control.valueChanges.pipe(
      startWith(control.value),
      map(() => {
        const allCountries = this._countryRegionDataService.countries;
        if (opts.excludeGeneric) {
          return allCountries.filter((country) => !country.isGeneric);
        }
        // Generic-first ordering: pin generic countries (e.g. XX) to the top,
        // keeping the already-alphabetical non-generic tail in place. Shared
        // mechanism for the employee/mobility fields that offer XX (US 7568681/92/715/731,
        // and the mobility fields next); fields that exclude XX never reach here.
        return [
          ...allCountries.filter((country) => country.isGeneric),
          ...allCountries.filter((country) => !country.isGeneric),
        ];
      }),
    );
  }

  public displayCountryNameAndCode(countryCode: string | null) {
    const name =
      countryCode != null
        ? this._countryName.findCountryName(countryCode)
        : null;
    return name != null ? `${name} (${countryCode})` : '';
  }

  public supportsRegionCalculations(countryCode: string | null) {
    const country =
      countryCode != null
        ? this._countryRegionDataService.findCountryByCode(countryCode)
        : null;
    // Generic countries (e.g. XX) have no real regions even when the reference
    // data flags region support (US 7770386). Keyed on isGeneric, never "XX".
    return country?.supportsRegionCalculations === true && !country.isGeneric;
  }

  public displayRegionNameAndCode(countryControl: FormControl<string | null>) {
    return (regionCode: string | null) => {
      const country =
        countryControl.value != null
          ? this._countryRegionDataService.findCountryByCode(
              countryControl.value,
            )
          : null;
      if (country != null && regionCode != null) {
        const regionName = this._regionName.findRegionName({
          countryCode: country.code,
          regionCode,
        });
        if (regionName != null) {
          return `${regionName} (${regionCode})`;
        }
      }
      return '';
    };
  }

  public init({
    countryControl,
    regionControl,
    onDestroy$,
    countryRegionValidation = true,
  }: {
    countryControl: FormControl<string | null | undefined>;
    regionControl?: FormControl<string | null | undefined>;
    onDestroy$: Observable<unknown>;
    countryRegionValidation?: boolean;
  }): void {
    const normalizeWhitespace = (
      control: FormControl<string | null | undefined>,
    ): void => {
      control.valueChanges.pipe(takeUntil(onDestroy$)).subscribe((value) => {
        if (value != null && value.trim() === '') {
          control.setValue(null, { emitEvent: false });
        }
      });
    };

    normalizeWhitespace(countryControl);

    if (regionControl == null) {
      return;
    }

    normalizeWhitespace(regionControl);

    if (!countryRegionValidation) {
      regionControl.clearValidators();
      regionControl.clearAsyncValidators();
      regionControl.updateValueAndValidity({ emitEvent: false });

      return;
    }

    // An existing period already on a generic country must not carry a region.
    if (
      countryControl.value != null &&
      regionControl.value != null &&
      this._countryRegionDataService.findCountryByCode(countryControl.value)
        ?.isGeneric === true
    ) {
      regionControl.setValue(null, { emitEvent: false });
    }

    countryControl.valueChanges
      .pipe(
        startWith(countryControl.value),
        distinctUntilChanged(),
        skip(1),
        takeUntil(onDestroy$),
      )
      .subscribe((countryCode) => {
        regionControl.markAsUntouched();
        regionControl.markAsPristine();
        regionControl.clearValidators();

        if (this.supportsRegionCalculations(countryCode ?? null)) {
          regionControl.setValidators([
            Validators.required,
            this.validRegionCode(countryCode),
          ]);
        }

        regionControl.setValue(null, { emitEvent: false });
        regionControl.updateValueAndValidity({ emitEvent: false });
      });
  }

  public getAllRegionOptions(): Observable<ApiClient.RegionDto[]> {
    return of(
      this._countryRegionDataService.countriesWithRegions.flatMap(
        (country) => country.regions,
      ),
    );
  }

  public displayIndependentRegionNameAndCode(
    regionCode: string | null,
  ): string {
    if (regionCode == null) {
      return '';
    }

    const region = this._countryRegionDataService.countriesWithRegions
      .flatMap((country) => country.regions)
      .find((candidate) => candidate.code === regionCode);

    return region != null ? `${region.name} (${region.code})` : '';
  }
  private validRegionCode(countryCode: string | null | undefined) {
    return (control: AbstractControl) => {
      if (control.value == null || countryCode == null) {
        return null;
      }
      const country =
        this._countryRegionDataService.findCountryByCode(countryCode);
      if (
        country != null &&
        country.regions.some((r) => r.code === control.value)
      ) {
        return null;
      }
      return {
        invalidRegion: control.value,
      };
    };
  }

  // Generic-first ordering for region options, mirroring the Section 0 country
  // top-pin in getCountryOptions: pin generic regions (region.isGeneric, e.g. the
  // XX generic region offered for US/IT/CA/CH) to the top while preserving the
  // already-alphabetical non-generic tail. Identified by the isGeneric flag, never
  // the literal "XX" (NFR-1). Non-region-supporting countries (incl. XX) never
  // reach here because callers gate on supportsRegionCalculations.
  private _genericRegionsFirst(
    regions: ApiClient.RegionDto[],
  ): ApiClient.RegionDto[] {
    return [
      ...regions.filter((region) => region.isGeneric),
      ...regions.filter((region) => !region.isGeneric),
    ];
  }

  public getSupportedRegionOptions(
    countryControl: FormControl<string | null>,
    regionControl: FormControl<string | null>,
  ) {
    return combineLatest([
      countryControl.valueChanges.pipe(startWith(countryControl.value)),
      regionControl.valueChanges.pipe(startWith(regionControl.value)),
    ]).pipe(
      map(([countryCtrlValue, regionCtrlValue]) => {
        const country =
          countryCtrlValue != null
            ? this._countryRegionDataService.findCountryByCode(countryCtrlValue)
            : null;
        if (country == null || !country.supportsRegionCalculations) {
          return [];
        }
        if (regionCtrlValue == null) {
          return this._genericRegionsFirst(country.regions);
        }
        const regionControlValueLower = regionCtrlValue.toLowerCase();
        return this._genericRegionsFirst(
          country.regions.filter(
            (r) =>
              r.code.toLowerCase().includes(regionControlValueLower) ||
              r.name.toLowerCase().includes(regionControlValueLower),
          ),
        );
      }),
    );
  }

  public getRegionOptions(
    countryControl: FormControl<string | null>,
    searchTerm = '',
  ): Observable<ApiClient.RegionDto[]> {
    const searchValue = searchTerm.trim().toLowerCase();

    return countryControl.valueChanges.pipe(
      startWith(countryControl.value),
      map((countryCode) => {
        const country =
          countryCode != null
            ? this._countryRegionDataService.findCountryByCode(countryCode)
            : null;

        const regions = country?.regions ?? [];

        return searchValue === ''
          ? this._genericRegionsFirst(regions)
          : this._genericRegionsFirst(
              regions.filter(
                (region) =>
                  region.name.toLowerCase().includes(searchValue) ||
                  region.code.toLowerCase().includes(searchValue),
              ),
            );
      }),
    );
  }

  public getAllRegionGroups(searchTerm = ''): Observable<RegionOptionGroup[]> {
    const searchValue = searchTerm.trim().toLowerCase();

    // Generic-first ordering of the country groups, same partition as
    // getCountryOptions (ADO 7770521). Keyed on isGeneric, never the literal "XX".
    const countries = this._countryRegionDataService.countriesWithRegions;
    const genericFirst = [
      ...countries.filter((country) => country.isGeneric),
      ...countries.filter((country) => !country.isGeneric),
    ];
    return of(
      genericFirst
        .map((country) => ({
          countryCode: country.code,
          countryName:
            this._countryName.findCountryName(country.code) ?? country.code,
          regions: this._genericRegionsFirst(
            searchValue === ''
              ? country.regions
              : country.regions.filter(
                  (region) =>
                    region.name.toLowerCase().includes(searchValue) ||
                    region.code.toLowerCase().includes(searchValue),
                ),
          ),
        }))
        .filter((group) => group.regions.length > 0),
    );
  }

  public displayAllRegionNameAndCode(regionCode: string | null): string {
    if (regionCode == null) {
      return '';
    }

    const region = this._countryRegionDataService.countriesWithRegions
      .flatMap((country) => country.regions)
      .find((candidate) => candidate.code === regionCode);

    return region == null ? '' : `${region.name} (${region.code})`;
  }
}
